import React, { useState, useLayoutEffect, useEffect } from 'react';
import {
  View,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  ActivityIndicator,
  Dimensions,
  TouchableWithoutFeedback,
  Platform,
  StatusBar,
} from 'react-native';
import Animated, {
  useSharedValue,
  interpolate,
  useAnimatedStyle,
  withTiming,
} from 'react-native-reanimated';
import { Color, CommonProperties, CommonStyles } from '../styles';
import { useSelector, useDispatch } from 'react-redux';
import { selectCompanyCurrencyCulture } from '../store/selectors/auth.selector';
import { formatCurrency } from '../utils/currency.utils';
import { rightAgentHeaderOption } from '../utils/defaultNavOptions';
import moment from 'moment';
import HeaderTitle from '../components/shared/HeaderTitle';
import FrontCreditCard from '../components/FrontCreditCard';
import BackCreditCard from '../components/BackCreditCard';
import BaseText from '../components/BaseText';
import { openAlert } from '../store/actions/alert';
import { getVirtualCardInfoApi } from '../services/aws/trip.service';
import * as ScreenOrientation from 'expo-screen-orientation';
import { HeaderBackButton } from '@react-navigation/elements';
import { scale } from 'react-native-size-matters';

const DEFAULT_PRECISION = 2;
const DEFAULT_CURRENY_CODE = 'en-US';

export default function EasyCardScreen({ navigation, route }) {
  const routeParams = route.params;
  const currencyculture = useSelector(selectCompanyCurrencyCulture);
  const { incompleteCardData, receiptResponse } = routeParams;
  const {
    CreditCardLastFourDigit,
    NameOnCard,
    SourceIdentifier,
    CreditCardVendorCode,
    VirtualPaymentSource,
    CreditCardExpiryMonth,
    CreditCardExpiryYear,
    isAir,
    isCar,
    isHotel,
  } = incompleteCardData;
  const { FromCity, FromDate, FriendlyName, ToDate, TripId } = receiptResponse;
  const [showCreditCardNumber, setShowCreditCardNumber] = useState(false);
  const [isCreditCardFetching, setIsCreditCardFetching] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const inputFormat = 'MMM DD, YY';
  const outputFormat = 'MMM DD, YYYY';
  const fromDate = moment(FromDate, inputFormat).format(outputFormat);
  const toDate = moment(ToDate, inputFormat).format(outputFormat);
  const creditCardExpiryMonth =
    CreditCardExpiryMonth < 10 ? `0${CreditCardExpiryMonth}` : CreditCardExpiryMonth;
  // mod operation to get last 2 digits of the year
  const creditCardExpiryYear = CreditCardExpiryYear % 100;
  const expiryDate = `${creditCardExpiryMonth}/${creditCardExpiryYear}`;
  const creditCardLastFourDigit = `************${CreditCardLastFourDigit}`;
  const [creditCardNumber, setCreditCardNumber] = useState(creditCardLastFourDigit);
  const [creditCardCVVNumber, setCreditCardCVVNumber] = useState('');
  const [creditCardName, setCreditCardName] = useState('');
  const [countdown, setCountdown] = useState(0);
  const rotate = useSharedValue(0);
  const dispatch = useDispatch();
  const [isPortrait, setIsPortrait] = useState(
    Dimensions.get('screen').height > Dimensions.get('screen').width
  );

  useEffect(() => {
    const unsubscribe = navigation.addListener('beforeRemove', () => {
      if (Platform.OS === 'web') {
        return;
      }
      ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP);
    });

    return unsubscribe;
  }, [navigation]);

  useEffect(() => {
    const updateOrientation = async () => {
      const currentOrientation = await ScreenOrientation.getOrientationAsync();
      const isPortraitNow = currentOrientation === ScreenOrientation.Orientation.PORTRAIT_UP;
      setIsPortrait(isPortraitNow);
      StatusBar.setHidden(!isPortraitNow);
    };

    updateOrientation();

    const subscription = ScreenOrientation.addOrientationChangeListener(updateOrientation);

    return () => {
      subscription.remove();
    };
  }, []);

  useLayoutEffect(() => {
    navigation.setOptions({
      headerShown: isPortrait,
      headerTitle: () => (
        <HeaderTitle title={`${FromCity} - ${FriendlyName}`} subtitle={`${fromDate} - ${toDate}`} />
      ),
      ...rightAgentHeaderOption({ navigation }),
      headerTitleAlign: 'center',
    });
  }, [isPortrait]);

  const rotateScreen = async () => {
    if (Platform.OS === 'web') {
      return;
    }

    const nextOrientation = isPortrait
      ? ScreenOrientation.OrientationLock.LANDSCAPE_RIGHT
      : ScreenOrientation.OrientationLock.PORTRAIT_UP;
    setIsPortrait(!isPortrait);
    await ScreenOrientation.lockAsync(nextOrientation);
  };

  const frontAnimatedStyles = useAnimatedStyle(() => {
    const rotateValue = interpolate(rotate.value, [0, 1], [0, 180]);
    return {
      transform: [
        {
          rotateY: withTiming(`${rotateValue}deg`, { duration: 1000 }),
        },
      ],
    };
  });

  const backAnimatedStyles = useAnimatedStyle(() => {
    const rotateValue = interpolate(rotate.value, [0, 1], [180, 360]);
    return {
      transform: [
        {
          rotateY: withTiming(`${rotateValue}deg`, { duration: 1000 }),
        },
      ],
    };
  });

  const BuildIssuedForAndCostLabel = (isAir, isCar, isHotel, receiptResponse) => {
    let issuedFor,
      totalCost,
      currencyCode,
      anyConditionMet = false;
    if (isAir) {
      if (receiptResponse?.AirResponseDetails) {
        const { AirlineName, TotalCost, CurrencyCode } = receiptResponse.AirResponseDetails;
        issuedFor = AirlineName;
        totalCost = TotalCost;
        currencyCode = CurrencyCode || DEFAULT_CURRENY_CODE;
        anyConditionMet = true;
      }
    } else if (isCar) {
      if (receiptResponse?.CarResponseDetails?.length > 0) {
        const { VendorName, TotalDecimal } = receiptResponse.CarResponseDetails[0];
        issuedFor = VendorName;
        totalCost = TotalDecimal;
        currencyCode = DEFAULT_CURRENY_CODE;
        anyConditionMet = true;
      }
    } else if (isHotel) {
      if (receiptResponse?.HotelResponseDetails?.length > 0) {
        const { HotelName, TotalCost, CurrencyCode } = receiptResponse.HotelResponseDetails[0];
        issuedFor = HotelName;
        totalCost = TotalCost;
        currencyCode = CurrencyCode || DEFAULT_CURRENY_CODE;
        anyConditionMet = true;
      }
    }
    if (!anyConditionMet) {
      return ['-', 0];
    }
    return [issuedFor, formatCurrency(totalCost, currencyCode, currencyculture, DEFAULT_PRECISION)];
  };

  const [issuedForLabel, totalCost] = BuildIssuedForAndCostLabel(
    isAir,
    isCar,
    isHotel,
    receiptResponse
  );

  const getVirtualCardInfo = async (tripKey, sourceType, sourceIdentifier) => {
    const message =
      'Oops! Something went wrong while fetching your card. Please double-check your internet connection and try again';
    try {
      const response = await getVirtualCardInfoApi(tripKey, sourceType, sourceIdentifier);
      if (!response) {
        dispatch(openAlert({ message }));
      }
      return response;
    } catch (error) {
      dispatch(openAlert({ message }));
      console.error(error);
      return null;
    }
  };

  const setShowFrontCardStates = () => {
    setIsDisabled(false);
    setCreditCardNumber(creditCardLastFourDigit);
    setCreditCardCVVNumber('');
    setCreditCardName('');
    setShowCreditCardNumber(false);
    setIsCreditCardFetching(false);
  };

  const setShowBackCardStates = (easyCard) => {
    const { CreditCardNumber, CreditCardCVVNumber, BillingFirstName, BillingLastName } = easyCard;
    setCreditCardNumber(CreditCardNumber);
    setCreditCardCVVNumber(CreditCardCVVNumber);
    setCreditCardName(`${BillingFirstName.toUpperCase()} ${BillingLastName.toUpperCase()}`);
    setIsCreditCardFetching(false);
    setShowCreditCardNumber(true);
  };

  const setIsFetchingCardStates = () => {
    setIsDisabled(true);
    setIsCreditCardFetching(true);
  };

  const flipCreditCard = () => {
    rotate.value = rotate.value ? 0 : 1;
  };

  const startCountdown = () => {
    const intervalId = setInterval(() => {
      setCountdown((prevCountdown) => {
        const newCountdown = prevCountdown - 1;

        // Check if the countdown has reached 0
        if (newCountdown === 0) {
          setShowFrontCardStates();
          flipCreditCard();
          clearInterval(intervalId);
        }

        return newCountdown;
      });
    }, 1000); // Update the countdown every second
  };

  const onPress = async () => {
    if (isDisabled) {
      return;
    }
    setIsFetchingCardStates();
    const easyCard = await getVirtualCardInfo(TripId, VirtualPaymentSource, SourceIdentifier);
    if (!easyCard) {
      setShowFrontCardStates();
      return;
    }
    setShowBackCardStates(easyCard);
    flipCreditCard();
    setCountdown(90);
    startCountdown();
  };

  return isPortrait ? (
    <>
      <SafeAreaView style={styles.container}>
        <View style={styles.wrapper}>
          <View>
            <BaseText style={styles.cardUsedStyle}>Card Used:</BaseText>
          </View>

          <View style={styles.cardsContainer}>
            <TouchableWithoutFeedback onPress={rotateScreen}>
              <Animated.View style={[styles.card, styles.frontCard, frontAnimatedStyles]}>
                <FrontCreditCard
                  creditCardNumber={creditCardLastFourDigit}
                  CreditCardVendorCode={CreditCardVendorCode}
                  isPortrait={isPortrait}
                />
              </Animated.View>
            </TouchableWithoutFeedback>

            <TouchableWithoutFeedback onPress={rotateScreen}>
              <Animated.View style={[styles.card, styles.backCard, backAnimatedStyles]}>
                <BackCreditCard
                  creditCardNumber={creditCardNumber}
                  CreditCardVendorCode={CreditCardVendorCode}
                  creditCardCVVNumber={creditCardCVVNumber}
                  creditCardName={creditCardName}
                  expiryDate={expiryDate}
                  isPortrait={isPortrait}
                />
              </Animated.View>
            </TouchableWithoutFeedback>
          </View>

          <View>
            <BaseText style={styles.dataTitleStyle}>Card Holder:</BaseText>
            <BaseText style={styles.dataStyle}>{NameOnCard}</BaseText>
          </View>
          <View>
            <BaseText style={styles.dataTitleStyle}>Issued For:</BaseText>
            <BaseText style={styles.dataStyle}>{issuedForLabel}</BaseText>
          </View>
          <View style={styles.columnsContainer}>
            <View style={styles.container}>
              <BaseText style={styles.dataTitleStyle}>Valid Thru:</BaseText>
              <BaseText style={styles.dataStyle}>{expiryDate}</BaseText>
            </View>
            <View style={styles.container}>
              <BaseText style={styles.dataTitleStyle}>Auth Amount:</BaseText>
              <BaseText style={styles.dataStyle}>{totalCost}</BaseText>
            </View>
          </View>
        </View>
      </SafeAreaView>
      <View style={styles.bottomButtonContainer}>
        <TouchableOpacity
          style={[styles.button, isDisabled && isCreditCardFetching && styles.disabledButton]}
          onPress={onPress}
        >
          <View style={styles.innerButton}>
            {!isCreditCardFetching && !showCreditCardNumber && (
              <View style={styles.columnsContainer}>
                <View style={styles.footerColumn}>
                  <BaseText style={styles.leftColumnText}>Reveal</BaseText>
                </View>
                <View style={styles.footerColumn}>
                  <BaseText style={styles.rightColumnText}>Number</BaseText>
                </View>
              </View>
            )}
            {!isCreditCardFetching && showCreditCardNumber && (
              <View style={styles.columnsContainer}>
                <View style={styles.footerColumn}>
                  <BaseText style={styles.leftSecondsRemaingText}>{countdown}</BaseText>
                </View>
                <View style={[styles.footerColumn, CommonStyles.ml2]}>
                  <BaseText style={styles.rightSecondsRemainingText}>seconds</BaseText>
                  <BaseText style={styles.rightSecondsRemainingText}>remaining</BaseText>
                </View>
              </View>
            )}
            {isCreditCardFetching && (
              <View style={styles.columnsContainer}>
                <View style={styles.loadingContainer}>
                  <ActivityIndicator color={Color.accent} size="large"></ActivityIndicator>
                </View>
              </View>
            )}
          </View>
        </TouchableOpacity>
      </View>
    </>
  ) : (
    <View style={styles.landscapeContainer}>
      <View style={styles.backButtonContainer}>
        <HeaderBackButton tintColor={Color.primary} onPress={() => navigation.goBack()} />
      </View>
      <View style={styles.cardsContainer}>
        <TouchableWithoutFeedback onPress={rotateScreen}>
          <Animated.View style={[styles.card, styles.frontCard, frontAnimatedStyles]}>
            <FrontCreditCard
              creditCardNumber={creditCardLastFourDigit}
              CreditCardVendorCode={CreditCardVendorCode}
              isPortrait={isPortrait}
            />
          </Animated.View>
        </TouchableWithoutFeedback>

        <TouchableWithoutFeedback onPress={rotateScreen}>
          <Animated.View style={[styles.card, styles.backCard, backAnimatedStyles]}>
            <BackCreditCard
              creditCardNumber={creditCardNumber}
              CreditCardVendorCode={CreditCardVendorCode}
              creditCardCVVNumber={creditCardCVVNumber}
              creditCardName={creditCardName}
              expiryDate={expiryDate}
              isPortrait={isPortrait}
            />
          </Animated.View>
        </TouchableWithoutFeedback>
      </View>
      <View>
        <TouchableOpacity
          style={[
            styles.button,
            styles.landscapeButton,
            isDisabled && isCreditCardFetching && styles.disabledButton,
          ]}
          onPress={onPress}
        >
          {!isCreditCardFetching && showCreditCardNumber && (
            <View>
              <BaseText style={styles.landscapeButtonText}>{countdown}</BaseText>
              <BaseText style={styles.landscapeButtonSecondsText} numberOfLines={2}>
                {'seconds\nremaining'}
              </BaseText>
            </View>
          )}
          {!isCreditCardFetching && !showCreditCardNumber && (
            <BaseText style={styles.landscapeRevealNumberText} numberOfLines={2}>
              {' '}
              {'Reveal\nNumber'}
            </BaseText>
          )}
          {isCreditCardFetching && (
            <View style={styles.loadingContainer}>
              <ActivityIndicator color={Color.accent} size="large"></ActivityIndicator>
            </View>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );
}

const revealNumberFontSize = scale(26);
const secondsRemainingFontSize = scale(26);
const columnTextCommonProperties = {
  color: Color.accent,
};
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Color.accent,
  },
  wrapper: {
    height: '100%',
  },
  cardUsedStyle: { marginTop: 20, marginLeft: 20, ...CommonProperties.h4 },
  dataTitleStyle: {
    marginTop: 20,
    marginLeft: 20,
    fontWeight: 'bold',
    ...CommonProperties.body,
  },
  dataStyle: { marginLeft: 20, ...CommonProperties.h4 },
  columnsContainer: {
    flex: 1,
    flexDirection: 'row',
  },
  footerColumn: { flex: 1, padding: 4, justifyContent: 'center' },
  bottomButtonContainer: { justifyContent: 'flex-end' },
  innerButton: {
    flexDirection: 'row',
    ...CommonProperties.centerAlignment,
    width: '100%',
  },
  button: {
    backgroundColor: Color.primary,
    height: scale(70),
    justifyContent: 'center',
    alignItems: 'center',
  },
  leftColumnText: {
    ...columnTextCommonProperties,
    textAlign: 'right',
    fontSize: revealNumberFontSize,
  },
  loadingContainer: { flex: 1, justifyContent: 'center', padding: 6 },
  rightColumnText: {
    ...columnTextCommonProperties,
    textAlign: 'left',
    fontSize: revealNumberFontSize,
  },
  leftSecondsRemaingText: {
    ...columnTextCommonProperties,
    fontSize: secondsRemainingFontSize,
    textAlign: 'right',
  },
  rightSecondsRemainingText: {
    ...columnTextCommonProperties,
    fontSize: secondsRemainingFontSize / 2,
    lineHeight: secondsRemainingFontSize / 2,
    textAlign: 'left',
  },
  disabledButton: {
    opacity: 0.7,
  },
  card: {
    width: '100%',
    backfaceVisibility: 'hidden',
  },
  cardsContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  frontCard: {
    position: 'absolute',
    backfaceVisibility: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
  },
  backCard: {
    backfaceVisibility: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
  },
  landscapeContainer: {
    flex: 1,
    flexDirection: 'row',
  },
  backButtonContainer: { padding: 10, marginTop: 10 },
  landscapeButton: { flex: 1, justifyContent: 'center', width: scale(100) },
  landscapeRevealNumberText: { color: Color.accent, fontSize: scale(18), lineHeight: scale(19) },
  landscapeButtonText: {
    fontSize: secondsRemainingFontSize,
    color: Color.accent,
    textAlign: 'center',
  },
  landscapeButtonSecondsText: {
    fontSize: secondsRemainingFontSize / 2,
    lineHeight: secondsRemainingFontSize / 2,
    color: Color.accent,
    textAlign: 'center',
  },
});
